import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { alpha, useTheme } from "@mui/material/styles";
import {
  format,
  isSameDay,
  startOfWeek,
  endOfWeek,
  startOfDay,
} from "date-fns";
import { getCalendarDaysForMonth, getCalendarDaysForWeek } from "../../../lib/dateUtils";
import { getWorkoutCalendarDate } from "../../../lib/firestoreDate";
import {
  healthReportBelongsToCalendarDay,
  reportHasHealthMetricsExcludingSyncedFitness,
  reportHasSyncedFitnessActivities,
} from "../../../lib/healthUtils";
import { HEALTH_ACTIVITY_ACCENT } from "./SyncedHealthActivitiesSection";
import NewColors from "../../../shared/theme/NewColors";

const WEEKDAYS_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_COMPACT = ["S", "M", "T", "W", "T", "F", "S"];

/** Completed Mighty45 workout on this calendar day. */
function dayHasTrainingWorkout(workouts, date) {
  if (!date || !workouts?.length) return false;
  return workouts.some((w) => {
    const workoutDate = getWorkoutCalendarDate(w);
    if (!workoutDate) return false;
    return isSameDay(workoutDate, date);
  });
}

function daySyncedHealthIndicators(healthReports, date) {
  let vitalsNutrition = false;
  let fitness = false;
  for (const r of healthReports || []) {
    if (!healthReportBelongsToCalendarDay(r, date)) continue;
    if (reportHasSyncedFitnessActivities(r)) fitness = true;
    if (reportHasHealthMetricsExcludingSyncedFitness(r)) vitalsNutrition = true;
  }
  return { vitalsNutrition, fitness };
}

/** Colored dots: training / macros / Apple Health fitness */
function DayMarkerDots({ hasTraining = false, hasHealthVitals = false, hasFitness = false, isSelected = false, dense = false }) {
  const theme = useTheme();
  if (!hasTraining && !hasHealthVitals && !hasFitness) return null;

  const ring = isSelected ? alpha(theme.palette.common.white, 0.85) : alpha(theme.palette.common.black, 0.12);
  const dotPx = dense ? 4.5 : 6;

  const trainingFill = isSelected ? alpha(theme.palette.common.white, 0.96) : theme.palette.primary.main;
  const vitalsFill = isSelected ? "#E1F5FE" : NewColors.status.info;
  const fitnessFill = isSelected ? "#BBF7D0" : HEALTH_ACTIVITY_ACCENT;

  const dot = (color) => (
    <Box
      sx={{
        width: dotPx,
        height: dotPx,
        borderRadius: "50%",
        bgcolor: color,
        boxShadow: `0 0 0 1px ${ring}`,
        flexShrink: 0,
      }}
    />
  );

  return (
    <Box
      aria-hidden
      sx={{
        display: "flex",
        gap: dense ? 0.28 : 0.375,
        mt: dense ? 0.2 : 0.35,
        minHeight: dense ? 6 : 7,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {hasTraining ? dot(trainingFill) : null}
      {hasHealthVitals ? dot(vitalsFill) : null}
      {hasFitness ? dot(fitnessFill) : null}
    </Box>
  );
}

function daySummaryLabel(date, bits) {
  if (!date) return "";
  const head = `${format(date, "EEEE, MMMM d")}`;
  return bits.length ? `${head}: ${bits.join(", ")}` : `${head}: no tracked activity`;
}

/** @param {{
 *   viewMonth: Date,
 *   selectedDay: Date | null,
 *   onSelectDay: (d: Date) => void,
 *   onChangeMonth: (delta: number) => void,
 *   onWeekNavigate: (delta: number) => void,
 *   viewMode: 'month' | 'week',
 *   onViewModeChange: (mode: 'month'|'week') => void,
 *   workouts: unknown[],
 *   healthReports: unknown[],
 *   loading: boolean,
 * }} props
 */
const MonthCalendar = ({
  viewMonth,
  selectedDay,
  onSelectDay,
  onChangeMonth,
  onWeekNavigate,
  viewMode,
  onViewModeChange,
  workouts,
  healthReports,
  loading,
}) => {
  const today = startOfDay(new Date());
  const isWeekMode = viewMode === "week";
  const anchorForWeek = selectedDay ? startOfDay(selectedDay) : today;

  const monthCells = getCalendarDaysForMonth(viewMonth);
  const weekCells = getCalendarDaysForWeek(anchorForWeek);

  const weekStart = startOfWeek(anchorForWeek, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(anchorForWeek, { weekStartsOn: 0 });
  const weekTitle = `${format(weekStart, "MMM d")} – ${format(weekEnd, "MMM d, yyyy")}`;
  const monthTitle = format(viewMonth, "MMMM yyyy");

  const weekdays = isWeekMode ? WEEKDAYS_COMPACT : WEEKDAYS_FULL;

  const renderEmptyMonthCell = (idx, compact) => (
    <Grid item xs={1} key={`month-pad-${idx}`}>
      <Box sx={{ aspectRatio: "1", maxHeight: compact ? 36 : 44, opacity: 0 }} aria-hidden />
    </Grid>
  );

  const renderDayCell = (date, compact) => {
    if (!date) {
      return null;
    }

    const dayNum = date.getDate();
    const isToday = isSameDay(date, today);
    const isSelected = selectedDay ? isSameDay(date, selectedDay) : false;
    const hasTraining = dayHasTrainingWorkout(workouts, date);
    const { vitalsNutrition: hasHealthVitals, fitness: hasFitness } = daySyncedHealthIndicators(healthReports, date);

    const emphasisTraining = Boolean(hasTraining);
    const emphasisFitness = !emphasisTraining && Boolean(hasFitness);

    const tintBgTraining = emphasisTraining ? (theme) => `${theme.palette.primary.main}12` : undefined;
    const tintBgFitness = emphasisFitness ? `${HEALTH_ACTIVITY_ACCENT}14` : undefined;

    const borderTraining = emphasisTraining ? (theme) => `${theme.palette.primary.main}80` : undefined;
    const borderFitness = emphasisFitness ? `${HEALTH_ACTIVITY_ACCENT}90` : undefined;

    const hoverTintTraining =
      emphasisTraining && !isSelected ? (theme) => `${theme.palette.primary.main}20` : undefined;
    const hoverTintFitness =
      emphasisFitness && !isSelected ? `${HEALTH_ACTIVITY_ACCENT}22` : undefined;

    const cellBgDefault = tintBgTraining ?? tintBgFitness ?? (isToday ? "action.hover" : "transparent");

    const cellBorderColor = borderTraining ?? borderFitness ?? (isToday ? "primary.main" : "transparent");
    const showBorder = isSelected || emphasisTraining || emphasisFitness || isToday;

    const bits = [];
    if (hasTraining) bits.push("Mighty45 workout");
    if (hasHealthVitals) bits.push("health & macros");
    if (hasFitness) bits.push("Apple Health fitness");

    const a11y = daySummaryLabel(date, bits);

    return (
      <Grid item xs={1} key={format(date, "yyyy-MM-dd")}>
        <Box
          component="button"
          type="button"
          aria-label={a11y}
          title={a11y}
          onClick={() => onSelectDay(date)}
          sx={{
            aspectRatio: "1",
            maxHeight: compact ? 38 : 44,
            minHeight: compact ? 34 : undefined,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            cursor: "pointer",
            backgroundColor: isSelected ? "primary.main" : cellBgDefault,
            color: isSelected ? "primary.contrastText" : "text.primary",
            border: showBorder ? "2px solid" : "none",
            borderColor: isSelected ? "primary.main" : cellBorderColor,
            py: compact ? 0.25 : 0,
            px: 0,
            m: 0,
            font: "inherit",
            textAlign: "center",
            "&:hover": {
              backgroundColor: isSelected ? "primary.dark" : hoverTintTraining ?? hoverTintFitness ?? "action.hover",
            },
            "&:focus-visible": {
              outline: (theme) => `2px solid ${theme.palette.secondary.main}`,
              outlineOffset: 1,
            },
          }}
        >
          <Typography
            variant={compact ? "caption" : "body2"}
            fontWeight={isToday || isSelected ? 700 : 500}
            sx={{ lineHeight: 1.1, fontSize: compact ? "0.76rem" : undefined }}
          >
            {dayNum}
          </Typography>
          <DayMarkerDots
            hasTraining={hasTraining}
            hasHealthVitals={hasHealthVitals}
            hasFitness={hasFitness}
            isSelected={Boolean(isSelected)}
            dense={compact}
          />
        </Box>
      </Grid>
    );
  };

  const compact = isWeekMode;

  const navPrevAria = isWeekMode ? "Previous week" : "Previous month";
  const navNextAria = isWeekMode ? "Next week" : "Next month";
  const onNavPrev = () => (isWeekMode ? onWeekNavigate(-1) : onChangeMonth(-1));
  const onNavNext = () => (isWeekMode ? onWeekNavigate(1) : onChangeMonth(1));

  return (
    <Card elevation={0} sx={{ overflow: "hidden" }}>
      <CardContent sx={{ py: compact ? 1 : 2, px: compact ? { xs: 1, sm: 1.25 } : { xs: 2, sm: 2.5 } }}>
        <Box sx={{ mb: compact ? 0.75 : 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <IconButton aria-label={navPrevAria} onClick={onNavPrev} size="small" sx={{ color: "primary.main", flexShrink: 0 }}>
              <NavigateBeforeIcon fontSize={compact ? "small" : "medium"} />
            </IconButton>

            <Typography
              variant={compact ? "subtitle1" : "h6"}
              fontWeight={700}
              color="primary"
              sx={{ flex: 1, textAlign: "center", letterSpacing: compact ? -0.1 : undefined }}
              component="div"
            >
              {isWeekMode ? weekTitle : monthTitle}
            </Typography>

            <IconButton aria-label={navNextAria} onClick={onNavNext} size="small" sx={{ color: "primary.main", flexShrink: 0 }}>
              <NavigateNextIcon fontSize={compact ? "small" : "medium"} />
            </IconButton>

            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: compact ? 0.5 : 1 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                size="small"
                sx={{
                  maxWidth: { xs: 320, sm: 280 },
                  flex: "1 1 auto",
                  "& .MuiToggleButton-root": {
                    flex: 1,
                    px: compact ? 0.85 : 1.25,
                  },
                }}
                onChange={(_, next) => {
                  if (!next) return;
                  onViewModeChange(next);
                }}
                aria-label="Calendar view density"
              >
                <ToggleButton value="month" sx={{ py: compact ? 0.45 : 0.65, fontWeight: 600, textTransform: "none" }}>
                  Month
                </ToggleButton>
                <ToggleButton value="week" sx={{ py: compact ? 0.45 : 0.65, fontWeight: 600, textTransform: "none" }}>
                  Week
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>

        {loading ? (
          <Typography color="text.secondary" align="center" py={compact ? 1 : 3}>
            Loading…
          </Typography>
        ) : (
          <>
            <Grid container columns={7} sx={{ mb: compact ? 0.25 : 0.5 }}>
              {weekdays.map((day, wi) => (
                <Grid item xs={1} key={`wd-${wi}`}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    align="center"
                    display="block"
                    sx={{ fontSize: compact ? "0.65rem" : undefined, letterSpacing: compact ? -0.2 : undefined }}
                  >
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            <Grid container columns={7} sx={{ rowGap: compact ? 0.5 : 1.25 }}>
              {isWeekMode
                ? weekCells.map(({ date }) => renderDayCell(date, true))
                : monthCells.map((cell, idx) => {
                    if (!cell.date) return renderEmptyMonthCell(idx, false);
                    return renderDayCell(cell.date, false);
                  })}
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MonthCalendar;
